import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Search,
  Filter,
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  Heart,
  CheckCircle2
} from 'lucide-react';
import { useApp } from '@/pages/providers/AppProvider';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export function JobsSection() {
  const navigate = useNavigate();
  const { jobs, applications, savedJobs, handleApplyJob, handleSaveJob } = useApp();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter jobs based on search
  const filteredJobs = jobs?.filter(job =>
    job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.location?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const hasApplied = (jobId: string) => {
    return applications?.some(a => a.jobId === jobId);
  };

  const isSaved = (jobId: string) => {
    return savedJobs?.includes(jobId);
  };

  const handleApply = async (jobId: string) => {
    try {
      await handleApplyJob(jobId);
      toast.success('Application submitted successfully!');
    } catch (error: any) {
      if (error?.code === 'PROFILE_INCOMPLETE') {
        navigate('/jobseeker-dashboard/resume');
        return;
      }
      toast.error(error?.message || 'Failed to apply');
    }
  };

  const handleSave = (jobId: string) => {
    try {
      handleSaveJob(jobId);
      toast.success(isSaved(jobId) ? 'Job removed from saved' : 'Job saved successfully!');
    } catch (error: any) {
      toast.error('Failed to save job');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Browse Jobs</h2>
        <p className="text-gray-600 mt-1">Find your next opportunity</p>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search jobs by title, company, or location..."
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="outline">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Jobs Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {filteredJobs.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="p-12 text-center">
                <Briefcase className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs found</h3>
                <p className="text-gray-500">Try adjusting your search criteria</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredJobs.map((job) => {
            const applied = hasApplied(job.id);
            const saved = isSaved(job.id);

            return (
              <Card key={job.id} className="hover:shadow-lg transition-all hover:scale-[1.02]">
                <CardContent className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleSave(job.id)}
                    >
                      <Heart
                        className={`h-4 w-4 ${saved ? 'fill-red-500 text-red-500' : 'text-gray-400'
                          }`}
                      />
                    </Button>
                  </div>

                  {/* Job Info */}
                  <h3 className="font-semibold text-base text-gray-900 mb-1 line-clamp-2 leading-tight">
                    {job.title}
                  </h3>
                  <p className="text-xs text-gray-600 mb-3 line-clamp-1">{job.company}</p>

                  {/* Details */}
                  <div className="space-y-1.5 mb-4">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{job.location || 'Remote'}</span>
                    </div>
                    {job.salary && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <DollarSign className="h-3 w-3" />
                        <span className="truncate">{job.salary}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span className="truncate">{job.type || 'Full-time'}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-auto">
                    {applied ? (
                      <Button size="sm" className="flex-1 text-xs h-8" disabled>
                        <CheckCircle2 className="h-3 w-3 mr-1.5" />
                        Applied
                      </Button>
                    ) : (
                      <Button size="sm" className="flex-1 text-xs h-8" onClick={() => handleApply(job.id)}>
                        Apply
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="text-xs h-8 px-2" onClick={() => navigate(`/jobs/${job.id}`)}>
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Stats Footer */}
      <Card>
        <CardContent className="p-4">
          <div className="text-center text-sm text-gray-600">
            Showing <span className="font-semibold">{filteredJobs.length}</span> of{' '}
            <span className="font-semibold">{jobs?.length || 0}</span> jobs
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
